/**
 * ReportConfigContext — form state for the Report Generator page.
 *
 * Mirrors MeetingConfigContext: sessionStorage persistence, an active report
 * type + its config, a company-sync effect, and a companySwitchCount signal
 * the page watches to reset its own view state. The config shape is owned by
 * the active report descriptor's `defaultConfig`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from './AppContext';
import { normalizeCompanyContext } from '../utils/companyContract';
import { REPORTS, DEFAULT_REPORT_ID, getReport } from '../data/reports/registry';

const ReportConfigContext = createContext(null);

export const useReportConfig = () => {
  const ctx = useContext(ReportConfigContext);
  if (!ctx) throw new Error('useReportConfig must be used within ReportConfigProvider');
  return ctx;
};

const SESSION_KEY = 'cs_report_config';

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/** Fresh config for a report id, optionally seeded from company context. */
function freshConfig(reportTypeId, companyContext) {
  const report = getReport(reportTypeId);
  const base = { ...report.defaultConfig };
  if (companyContext && typeof report.deriveConfigFromCompany === 'function') {
    const derived = report.deriveConfigFromCompany(companyContext) || {};
    Object.keys(derived).forEach((k) => {
      if (derived[k]) base[k] = derived[k];
    });
  }
  return base;
}

export const ReportConfigProvider = ({ children }) => {
  const { activeCompany } = useApp();
  const companyContext = useMemo(() => normalizeCompanyContext(activeCompany || {}), [activeCompany]);

  // Read the session snapshot once on mount.
  const sessionSnapshot = useRef(readSession());
  const skipSyncRef = useRef(Boolean(sessionSnapshot.current));

  const [reportTypeId, setReportTypeId] = useState(
    () => sessionSnapshot.current?.reportTypeId || DEFAULT_REPORT_ID,
  );
  const [config, setConfig] = useState(
    () => sessionSnapshot.current?.config
      || freshConfig(sessionSnapshot.current?.reportTypeId || DEFAULT_REPORT_ID, null),
  );

  // Company-switch signal — the page watches this to reset its view state.
  const [companySwitchCount, setCompanySwitchCount] = useState(0);
  const prevCinRef = useRef(activeCompany?.cin || null);

  // ── Company sync ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeCompany) return;
    const isSwitch = Boolean(prevCinRef.current) && prevCinRef.current !== activeCompany.cin;
    prevCinRef.current = activeCompany.cin;

    // First load from a restored session — keep the restored config untouched.
    if (skipSyncRef.current && !isSwitch) {
      skipSyncRef.current = false;
      return;
    }
    skipSyncRef.current = false;

    const report = getReport(reportTypeId);
    const derived = typeof report.deriveConfigFromCompany === 'function'
      ? (report.deriveConfigFromCompany(companyContext) || {})
      : {};
    setConfig((prev) => {
      const next = { ...prev };
      Object.keys(derived).forEach((k) => {
        // On a company switch, company-derived fields are overwritten; on first
        // load only empty fields are filled so the user's edits always win.
        if ((isSwitch || !String(next[k] || '').trim()) && derived[k]) {
          next[k] = derived[k];
        }
      });
      return next;
    });
    if (isSwitch) setCompanySwitchCount((c) => c + 1);
  }, [activeCompany, companyContext, reportTypeId]);

  // ── Listed companies → Key Audit Matters is mandatory ─────────────────────
  useEffect(() => {
    if (config.companyType === 'listed' && !config.includeKAM) {
      setConfig((prev) => ({ ...prev, includeKAM: true }));
    }
  }, [config.companyType, config.includeKAM]);

  // ── Persist to sessionStorage (debounced) ─────────────────────────────────
  const sessionTimerRef = useRef(null);
  useEffect(() => {
    clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ reportTypeId, config }));
      } catch { /* quota — ignore */ }
    }, 150);
  }, [reportTypeId, config]);

  // ── Setters ───────────────────────────────────────────────────────────────
  const updateConfig = useCallback((field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setReportType = useCallback((id) => {
    setReportTypeId(id);
    setConfig(freshConfig(id, companyContext));
  }, [companyContext]);

  // Bulk restore from a saved draft ({ reportTypeId, reportConfig }).
  const restoreConfig = useCallback((payload) => {
    if (!payload) return;
    const id = payload.reportTypeId || reportTypeId;
    if (payload.reportTypeId) setReportTypeId(payload.reportTypeId);
    if (payload.reportConfig) {
      const report = getReport(id);
      setConfig({ ...report.defaultConfig, ...payload.reportConfig });
    }
    skipSyncRef.current = true; // prevent company-sync from clobbering the restore
  }, [reportTypeId]);

  const resetToDefaults = useCallback(() => {
    setConfig(freshConfig(reportTypeId, companyContext));
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }, [reportTypeId, companyContext]);

  const report = useMemo(() => getReport(reportTypeId), [reportTypeId]);

  // Payload for the optional AI polish.
  const buildPayload = useCallback(
    () => ({ reportTypeId, config, companyContext }),
    [reportTypeId, config, companyContext],
  );

  const value = useMemo(() => ({
    reportTypeId, config, report, reports: REPORTS, companyContext, companySwitchCount,
    updateConfig, setReportType, restoreConfig, resetToDefaults, buildPayload,
  }), [
    reportTypeId, config, report, companyContext, companySwitchCount,
    updateConfig, setReportType, restoreConfig, resetToDefaults, buildPayload,
  ]);

  return (
    <ReportConfigContext.Provider value={value}>
      {children}
    </ReportConfigContext.Provider>
  );
};

export default ReportConfigContext;
